import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { Router } from '@angular/router';
import { Observable, from, of, filter, Subject, Subscription } from 'rxjs';
import { map, switchMap, concatMap, debounceTime } from 'rxjs/operators';

import { LazyLoadEvent, MenuItem } from 'primeng/api';
import { Table } from 'primeng/table';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { ClientApiService } from 'src/app/services/client-api.service';
import { PerListEntry } from '../shared/per-list-entry';
import { Person } from '../shared/person';

/** Column descriptor for the persons PrimeNG table. */
export class column {
  field = '';
  header = '';
  nonhide: boolean;
  show: boolean;
  filter: boolean;
};

@Component({
  selector: 'app-persons',
  templateUrl: './persons.component.html',
  styleUrls: ['./persons.component.css',],
  encapsulation: ViewEncapsulation.None
})
/**
 * Person (contact) list page — mirrors `ClientsComponent` structure.
 *
 * Driven by three Subjects:
 * - `clients$` — full reload with `debounceTime(300)` → `switchMap(getPerListSliceFilter)`.
 * - `cliPage$` — lazy page slice emitted by PrimeNG's `onLazyLoad`.
 * - `update$` — async enrichment pipeline that sets `personLastName` to
 *   `'>> Без фамилии <<'` for rows where the last name is missing.
 *
 * Column visibility is persisted to `localStorage` under `storeNameShowRowOptions`
 * (`'persons-columns-1'`) on `ngOnDestroy`.
 */
export class PersonsComponent implements OnInit, OnDestroy {

  private globalSettinsData: GlobalSettingsData;

  /*
SELECT [PERSID]
      ,[PERSCLIID]
      ,[PERSONDEPT]
      ,[personSaveAs]
      ,[personFullName]
      ,[personLastName]
      ,[personFirstName]
      ,[personMiddleName]
      ,[personCompany]
      ,[personDepartment]
      ,[personTitle]
      ,[personAddress]
      ,[personAddress2]
      ,[personCity]
      ,[personPostCode]
      ,[personCountry]
      ,[personWorkPhone]
      ,[personWorkPhone2]
      ,[personWorkFax]
      ,[personWorkFax2]
      ,[personMobilePhone]
      ,[personCompanyId]
      ,[personPersonalId]
      ,[PERSONEMAIL]
  FROM [dbo].[webPersonsList]

*/

  personslist: PerListEntry[];
  datasource: PerListEntry[];
  person: Person;

  clientSubsQuery: Subscription;
  cliPageQuery: Subscription;
  updNameQuery: Subscription;

  personslistentry: PerListEntry;

  totalRecords: number;

  rows: number = 25;

  loading: boolean = true;

  private storeNameFilterOptions: string = 'persons-filters-1';

  cfs: any[string] = [];

  pf: string[] = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '',];

  cf01 = '';
  cf02 = '';
  cf03 = '';
  cf04 = '';
  cf05 = '';
  cf06 = '';
  cf07 = '';
  cf08 = '';
  cf09 = '';
  cf10 = '';
  cf11 = '';
  cf12 = '';
  cf13 = '';
  cf14 = '';
  cf15 = '';
  cf16 = '';
  cf17 = '';
  cf18 = '';
  cf19 = '';
  cf20 = '';
  cf21 = '';
  cf22 = '';

  items: MenuItem[];

  @ViewChild('dt') table: Table;

  search$: Observable<string>;
  private clients$ = new Subject<any>();
  private cliPage$ = new Subject<{ from: number, to: number, option: any }>();
  private update$ = new Subject<PerListEntry[]>();

  private storeNameShowRowOptions: string = 'persons-columns-1';

  showPersRowsOptions = [
    { name: 'ФИО', value: 1 },
    { name: 'Организация', value: 2 },
    { name: 'Телефоны', value: 4 },
    { name: 'Адрес', value: 8 },
    { name: 'Примечание', value: 16 },
  ];

  showRowOptions: number[] = [];

  /** `true` when the persons list is empty. */
  public get noItems(): boolean { return ((this.personslist || []).length < 1); }

  /** `true` when the "Full name" column group is selected for display. */
  public get seeNames(): boolean { return this.showRowOptions.includes(1); }
  /** `true` when the "Company" column group is selected for display. */
  public get seeCompany(): boolean { return this.showRowOptions.includes(2); }
  /** `true` when the "Phones" column group is selected for display. */
  public get seePhones(): boolean { return this.showRowOptions.includes(4); }
  /** `true` when the "Address" column group is selected for display. */
  public get seeAddress(): boolean { return this.showRowOptions.includes(8); }
  /** `true` when the "Notes" column group is selected for display. */
  public get seeNotes(): boolean { return this.showRowOptions.includes(16); }

  constructor(
    private cliApi: ClientApiService,
    private router: Router,
    private titleService: Title
  ) {
    this.items = [
      { label: 'Открыть/Редактировать', icon: 'pi pi-fw pi-user-edit', command: () => this.viewCliListEntry(this.personslistentry) },
      { label: 'Удалить', icon: 'pi pi-fw pi-times', command: () => this.deleteCliListEntry(this.personslistentry) }
    ];
  }

  /** Persists column-visibility options to localStorage and unsubscribes all queries. */
  ngOnDestroy(): void {
    console.log('*** PersonsComponent in ngOnDestroy');

    localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));

    if (this.clientSubsQuery) { this.clientSubsQuery.unsubscribe(); }

    if (this.cliPageQuery) { this.cliPageQuery.unsubscribe(); }

    if (this.updNameQuery) { this.updNameQuery.unsubscribe(); }
  }

  /** Wires up the three Subjects, restores saved column-visibility, and triggers the initial load. */
  ngOnInit(): void {
    console.log('*** PersonsComponent in ngOnInit');

    this.titleService.setTitle('Контакты');

    let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
    if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }
    console.log('*** PersonsComponent in ngOnInit: ' + JSON.stringify(this.showRowOptions));

    //let _storedFilterOptions: string = localStorage.getItem(this.storeNameFilterOptions);

    this.updNameQuery =
      this.update$
        .pipe(
          switchMap(list => from(list)),
          filter(cl => !(cl && cl.personLastName && (cl.personLastName.trim().length > 0))),
          concatMap(cl => {
            cl.personLastName = '>> Без фамилии ' + cl.PERSID + ' <<';

            //if (!(cl && cl.personLastName && (cl.personLastName.trim().length > 0))) {
            //}
            return of(cl);
          })
        )
        .subscribe();

    this.cliPageQuery =

      this.cliPage$.pipe(
        switchMap((opt: any) => {

          this.loading = true;
          return this.cliApi.getPerListSliceFilter(opt.from, opt.to, opt.option)
            .pipe(
              map(data => {

              this.personslist = data;
              this.update$.next(this.personslist);

              this.loading = false;

            }));
        })).subscribe();

    this.clientSubsQuery =

      this.clients$
        .pipe(
          debounceTime(300),
          switchMap(options => {

            this.loading = true;
            return this.cliApi.getPerListSliceFilter(0, 1000000, options)
              .pipe(map((data: PerListEntry[]) => {
                this.totalRecords = data.length;
                this.personslist = data.slice(this.table ? this.table.first : 0, this.table ? this.table.rows : this.rows);

                this.update$.next(this.personslist);

                this.loading = false;
              }));
          })
        )
        .subscribe();

    let getoptions: any = {};

    if (this.table) {
      //console.log('*** ProductListComponent in createProductSubscription: before spread prodtable filter' + JSON.stringify( getoptions));
      getoptions = {
        sp: {
          sf: this.table.sortField,
          so: this.table.sortOrder
        },
        fs: this.table.filters
      }
      //console.log('*** ProductListComponent in createProductSubscription: before after spread prodtable filter' + JSON.stringify(getoptions));
    }

    this.clients$.next(getoptions);
  }

  /** PrimeNG `onLazyLoad` handler — pushes the requested page window to `cliPage$`. */
  loadClientList(event: LazyLoadEvent) {
    //in a real application, make a remote request to load data using state metadata from event
    //event.first = First row offset
    //event.rows = Number of rows per page
    //event.sortField = Field name to sort with
    //event.sortOrder = Sort order as number, 1 for asc and -1 for dec
    //filters: FilterMetadata object having field as key and filter value, filter matchMode as value

    console.log('*** PersonsComponent Lazy Load initiated');
    console.log(event.filters);

    this.cliPage$.next({
      from: event.first, to: (event.first + event.rows), option: {
        sp: {
          sf: event.sortField,
          so: event.sortOrder
        },
        fs: { ...event.filters }
      }
    });
  }

  /** Triggers a full reload via `clients$` whenever a column filter value changes. */
  filterProdTable(event) {
    console.log('*** ProductListComponent filterProdTable: ' + JSON.stringify(event.filters));
    this.clients$.next({
      sp: {
        sf: this.table.sortField,
        so: this.table.sortOrder
      },
      fs: this.table.filters
    });
  }

  /** No-op handler for row selection (currently disabled). */
  onRowSelect(event) {
    //console.log('*** PersonsComponent onRowSelect: ' + JSON.stringify(event));
  }

  /** No-op handler for row unselection. */
  onRowUnselect(event) {
    //console.log('*** PersonsComponent onRowUnselect: ' + JSON.stringify(event));
  }

  /** Navigates to the person editor for the selected list entry. */
  viewCliListEntry(cle: PerListEntry) {
    console.log('*** PersonsComponent viewCliListEntry: ' + JSON.stringify(cle));
    this.router.navigate(['/edit-person', cle.PERSID]);
  }

  /** Navigates to the person editor in new-person mode (id 0). */
  onCreateNewClient(event) {
    console.log('*** PersonsComponent onCreateNewClient: ' + JSON.stringify(event))
    this.router.navigate(['/edit-person', 0]);
  }

  /** Delete stub — not yet implemented. */
  deleteCliListEntry(cle: PerListEntry) {
    //this.products = this.products.filter((p) => p.id !== product.id);
    //this.messageService.add({ severity: 'info', summary: 'Product Deleted', detail: product.name });
    //this.selectedProduct = null;
  }

  /** PrimeNG table state-save callback (currently a no-op). */
  onStateSave(state) {
    //    console.log('*** ClientsComponent onStateSave: ' + JSON.stringify(state));
  }

  /** Restores filter values and row count from a previously saved table state. */
  onStateRestore(state) {
    //console.log('*** ClientsComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cfs[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }

    if (state.rows) {
      this.rows = state.rows;
    }
  }

  /** Clears all table filters, resets state, and triggers a full reload via `clients$`. */
  clearOrResetTableFilter(event) {
    console.log('*** PersonsComponent clearOrResetTableFilter: ' + JSON.stringify(event));

    this.table.clearState();
    this.table.clear();
    this.table.reset();

    this.table.filters = {};

    for (let f in this.cfs) {
      this.cfs[f] = "";
    }

    this.clients$.next({});
  }

  /** Forces a full reload while preserving the current sort and filter state. */
  onReloadAllData(event) {
    console.log('*** PersonsComponent onReloadAllData: ' + JSON.stringify(event));

    let getoptions: any = {};

    if (this.table) {
      getoptions = {
        sp: {
          sf: this.table.sortField,
          so: this.table.sortOrder
        },
        fs: this.table.filters
      }
    }
    this.clients$.next(getoptions);
  }

  /** Handles a change in the row-display options multiselect (currently a no-op). */
  onRowsOptionChange(event) {
    //console.log('*** PersonsComponent in onRowsOptionChange: ' + JSON.stringify(event));
    //console.log('*** PersonsComponent in onRowsOptionChange: ' + JSON.stringify(this.showRowOptions));
  }
}
